import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { STRIPE_PROXY_URL, PRICES, METERED_PRICES } from '../../config/billing';

interface CreateSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  defaultCustomerId?: string;
  defaultPropertyId?: number | string;
  customers: Array<{ id: number; company_name: string; stripe_customer_id?: string }>;
  onCreated?: () => void;
}

const PRICE_OPTIONS = [
  { label: 'Property Base ($0/mo)', price: PRICES.PROPERTY_BASE, metered: false },
  { label: 'Per Device ($0/mo)', price: PRICES.PER_DEVICE, metered: false },
  { label: 'Per Unit ($0/mo)', price: PRICES.PER_UNIT, metered: false },
  { label: 'Per Talk Minute (metered)', price: METERED_PRICES.PER_TALK_MINUTE, metered: true },
  { label: 'Per Video Minute (metered)', price: METERED_PRICES.PER_VIDEO_MINUTE, metered: true },
];

export const CreateSubscriptionModal: React.FC<CreateSubscriptionModalProps> = ({
  open, onClose, defaultCustomerId, defaultPropertyId, customers, onCreated,
}) => {
  const [customerId, setCustomerId] = useState('');
  const [items, setItems] = useState<Array<{ priceIdx: number; quantity: number }>>([{ priceIdx: 0, quantity: 1 }]);
  const [propertyId, setPropertyId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setCustomerId(defaultCustomerId || '');
      setPropertyId(defaultPropertyId ? String(defaultPropertyId) : '');
      setItems([{ priceIdx: 0, quantity: 1 }]);
      setError('');
    }
  }, [open, defaultCustomerId, defaultPropertyId]);

  const stripeCusts = customers.filter(c => c.stripe_customer_id);

  const handleSubmit = async () => {
    if (!customerId) { setError('Select a customer'); return; }
    setSubmitting(true);
    setError('');
    try {
      const priceIds = items.map(i => ({
        price: PRICE_OPTIONS[i.priceIdx].price,
        quantity: PRICE_OPTIONS[i.priceIdx].metered ? undefined : i.quantity,
      }));
      const res = await fetch(`${STRIPE_PROXY_URL}/subscriptions/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customerId,
          price_ids: priceIds,
          metadata: propertyId ? { property_id: propertyId } : {},
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Failed'); }
      onCreated?.();
      onClose();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Create Subscription" size="lg">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={customerId}
            onChange={e => setCustomerId(e.target.value)}
          >
            <option value="">Select customer...</option>
            {stripeCusts.map(c => (
              <option key={c.stripe_customer_id} value={c.stripe_customer_id}>
                {c.company_name} ({c.stripe_customer_id})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Line Items</label>
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-2">
              <select
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={item.priceIdx}
                onChange={e => {
                  const next = [...items];
                  next[idx] = { ...next[idx], priceIdx: Number(e.target.value) };
                  setItems(next);
                }}
              >
                {PRICE_OPTIONS.map((p, i) => <option key={i} value={i}>{p.label}</option>)}
              </select>
              {!PRICE_OPTIONS[item.priceIdx].metered && (
                <input
                  type="number" min={1}
                  className="w-20 border border-gray-300 rounded-lg px-2 py-2 text-sm"
                  value={item.quantity}
                  onChange={e => {
                    const next = [...items];
                    next[idx] = { ...next[idx], quantity: Number(e.target.value) || 1 };
                    setItems(next);
                  }}
                />
              )}
              {items.length > 1 && (
                <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-red-500 text-sm">✕</button>
              )}
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={() => setItems([...items, { priceIdx: 0, quantity: 1 }])}>+ Add Item</Button>
        </div>

        <Input label="Property ID (optional)" value={propertyId} onChange={e => setPropertyId(e.target.value)} />

        {error && <p className="text-red-600 text-sm">{error}</p>}

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting}>{submitting ? 'Creating...' : 'Create Subscription'}</Button>
        </div>
      </div>
    </Modal>
  );
};
